/**
 * Теги товаров — именованные списки товаров, общие для всех (разметка
 * 29.09.26 15:45). Спека — `docs/specs/2026-09-29-product-tags-design.md`.
 */

import { RESET_PARAM } from "@/lib/productNav";

/** Параметр адреса «Товаров»: отбор по тегам `12,34,-15` (режет сервер, `tagop` — и/или). */
export const TAG_PARAM = "tag";
/** Как сходятся включённые теги: `and` — во всех, `or` (умолчание, в адрес не пишется) — в любом. */
export const TAGOP_PARAM = "tagop";

/** Список тегов для отбора: `null` — едет или не нужен, `false` — не приехал (`error`). */
export type TagList = { tags: Tag[] | null | false; error: string };

export type Tag = {
  id: number;
  name: string;
  count: number;
  user: string;
  created: string | null;
  modified: string | null;
  /** Сколько из спрошенных товаров уже в теге (только в списке). */
  hit?: number;
};

export type TagFull = Tag & { product_ids: number[] };

/** Состояние чекбокса тега против отметки: все отмеченные в нём, часть или ни одного. */
export type TagCheck = "all" | "some" | "none";

export function tagCheck(hit: number, picked: number): TagCheck {
  if (picked === 0 || hit === 0) return "none";
  return hit >= picked ? "all" : "some";
}

/** Отбор по тегам: `on: "in"` — тег включён, `"out"` — исключён (`-12` в адресе). */
export type TagPick = { id: number; on: "in" | "out" };

/** Больше тегов в отборе сервер не принимает (`MAX_TAGS` в `catalog/tag/filter.py`). */
export const MAX_TAG_PICKS = 50;

/**
 * Строка отбора `12,34,-15` в канон сервера (`TagFilter.key()`): включённые по
 * возрастанию, затем исключённые со знаком минус. Мусор — `null`: номер не целое
 * положительное, один тег и включён и исключён, больше 50 тегов. Фронт не шлёт
 * то, что сервер ответит 422.
 */
export function cleanTagParam(raw: string | null | undefined): string | null {
  if (!raw) return null;
  const into = new Set<number>();
  const out = new Set<number>();
  for (const word of raw.split(",")) {
    // Как сервер (`_NUM`): не больше 10 цифр, иначе 422.
    if (!/^-?\d{1,10}$/.test(word)) return null;
    const n = Number(word);
    if (n === 0 || !Number.isSafeInteger(n)) return null;
    (n < 0 ? out : into).add(Math.abs(n));
  }
  for (const n of into) if (out.has(n)) return null;
  if (into.size + out.size > MAX_TAG_PICKS) return null;
  return tagParam([
    ...[...into].map((id): TagPick => ({ id, on: "in" })),
    ...[...out].map((id): TagPick => ({ id, on: "out" })),
  ]);
}

/** Отбор из строки адреса; мусор и пусто — пустой список. */
export function tagPicks(value: string | null): TagPick[] {
  const clean = cleanTagParam(value);
  if (clean === null) return [];
  return clean.split(",").map((w) => ({ id: Math.abs(Number(w)), on: w.startsWith("-") ? "out" : "in" }));
}

/** Строка отбора для адреса и запроса в каноне сервера; пусто — `null`. */
export function tagParam(picks: readonly TagPick[]): string | null {
  const ids = (on: TagPick["on"]) =>
    [...new Set(picks.filter((p) => p.on === on).map((p) => p.id))].sort((a, b) => a - b);
  const words = [...ids("in").map(String), ...ids("out").map((n) => `-${n}`)];
  return words.length ? words.join(",") : null;
}

/**
 * Плашка тега отбора: имя из списка со знаком («+ Лето», «− Брак»). Номер из
 * `missing` (ответ сервера) или неизвестный приехавшему списку — «тег N удалён»:
 * тег из сохранённых настроек, убранный соседом, иначе ломал бы раздел молча.
 * Список ещё едет или не приехал (`null`/`false`) — номером, без приговора.
 */
export function tagPickLabel(
  pick: TagPick,
  tags: readonly Tag[] | null | false,
  missing: readonly number[],
): { text: string; gone: boolean } {
  const тег = tags ? tags.find((t) => t.id === pick.id) : undefined;
  if (missing.includes(pick.id) || (tags && !тег)) return { text: `тег ${pick.id} удалён`, gone: true };
  return { text: `${pick.on === "in" ? "+" : "−"} ${тег ? тег.name : `тег ${pick.id}`}`, gone: false };
}

/** Как сходятся включённые словом; меньше двух включённых — режима нет, `null`. */
export function tagOpWord(picks: readonly TagPick[], op: "and" | "or"): string | null {
  return picks.filter((p) => p.on === "in").length >= 2 ? (op === "and" ? "и" : "или") : null;
}

/**
 * Отбор после правки плашками: строка в каноне и режим. Режим человека держится,
 * пока в отборе есть хоть один тег: исключил второй тег и вернул — «и» на месте.
 * Пустой отбор — `or`: режима без тегов нет (`writeToolbar` его и не пишет).
 */
export function tagFilterValue(
  picks: readonly TagPick[],
  op: "and" | "or",
): { value: string | null; op: "and" | "or" } {
  const value = tagParam(picks);
  return { value, op: value === null ? "or" : op };
}

/**
 * Ссылка тега — экран «Товары» с отбором тега. `reset=1` снимает отбор
 * тулбара (категория, цвет, план, потребность): иначе товар тега из другой
 * категории молча пропал бы с экрана.
 */
export function tagHref(id: number): string {
  return `/products?${TAG_PARAM}=${id}&${RESET_PARAM}=1`;
}

/** Полная ссылка — для копирования в чат. */
export function tagUrl(id: number, origin: string): string {
  return `${origin}${tagHref(id)}`;
}

export const tagsApi = (productIds: readonly number[] = []): string =>
  productIds.length ? `/api/tags?product_ids=${productIds.join(",")}` : "/api/tags";

export const tagApi = (id: number): string => `/api/tags/${id}`;

/** Поиск по имени: без регистра, подстрока. */
export function filterTags(tags: readonly Tag[], q: string): Tag[] {
  const s = q.trim().toLowerCase();
  return s ? tags.filter((t) => t.name.toLowerCase().includes(s)) : [...tags];
}
